import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./SurveyTableComponent.css";

function normalizeText(s) {
  if (typeof s !== "string") return s;
  return s.replaceAll("\\u002F", "/");
}

function makeId(q, suffix = "") {
  const base = q.name || `q_${q.qid}`;
  return suffix ? `${base}__${suffix}` : base;
}

function normalizeKey(s) {
  return s
    ?.toString()
    ?.trim()
    ?.normalize("NFD")
    ?.replace(/\p{Diacritic}/gu, "")
    ?.replace(/[’']/g, "'")
    ?.replace(/\s+/g, " ")
    ?.toLowerCase();
}


async function loadMatrixSpecFromFormHtml() {
  const res = await fetch("/Form/form.html", { cache: "no-store" });
  if (!res.ok) throw new Error("form.html introuvable");
  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, "text/html");
  const spec = {};

  const labelNodes = Array.from(doc.querySelectorAll('label[id^="label_"]'));
  labelNodes.forEach((lab) => {
    const id = lab.id;
    const m = id.match(/^label_(\d+)_(row|col)_(\d+)$/);
    if (!m) return;
    const qid = String(m[1]);
    const kind = m[2];
    const idx = Number(m[3]);
    spec[qid] = spec[qid] || { rows: [], cols: [] };
    const text = lab.textContent?.trim() ?? "";
    if (kind === "row") spec[qid].rows[idx] = text;
    else spec[qid].cols[idx] = text;
  });

  Object.keys(spec).forEach((qid) => {
    spec[qid].rows = (spec[qid].rows || []).filter(Boolean);
    spec[qid].cols = (spec[qid].cols || []).filter(Boolean);
  });

  return spec;
}

function QuestionItem({ q, value, onChange, matrixSpec, highlightedAnswers }) {
  const name = q.name || `q_${q.qid}`;
  const text = normalizeText(q.text);
  const legendId = makeId(q, "legend");

  const styleSelected = {
    color: "#27AE60",
    fontWeight: 700
  };

  const clean = (s) =>
    s
      ?.toString()
      ?.trim()
      ?.normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .replace(/[’']/g, "'")
      .toLowerCase();

  switch (q.type) {
    case "control_radio":
    case "control_checkbox": {
      return (
        <article className="survey2col__question">
          <header className="survey2col__question-head">
            <h4 className="survey2col__question-title" id={legendId}>
              {text}
            </h4>
          </header>
          {q.choices?.length ? (
            <fieldset
              className="survey2col__fieldset"
              aria-describedby={legendId}
              aria-disabled="true"
            >
              <ul
                className="survey2col__choices"
                style={{ listStyle: "none", margin: 0, paddingLeft: 0 }}
              >
                {q.choices.map((opt, idx) => {
                  const id = makeId(q, `opt${idx}`);
                  const choiceNorm = clean(opt);

                  let picked = false;
                  if (highlightedAnswers instanceof Set) {
                    for (const a of highlightedAnswers) {
                      if (clean(a) === choiceNorm) {
                        picked = true;
                        break;
                      }
                    }
                  } else if (Array.isArray(highlightedAnswers)) {
                    picked = highlightedAnswers.some(
                      (a) => clean(a) === choiceNorm
                    );
                  } else if (typeof highlightedAnswers === "string") {
                    picked = clean(highlightedAnswers) === choiceNorm;
                  }

                  return (
                    <li key={idx}>
                      <label
                        className="choice"
                        htmlFor={id}
                        style={picked ? styleSelected : undefined}
                      >
                        <input
                          id={id}
                          type={q.type === "control_radio" ? "radio" : "checkbox"}
                          name={name}
                          value={opt}
                          checked={picked}
                          disabled
                          readOnly
                        />
                        <span>{opt}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </fieldset>
          ) : null}
        </article>
      );
    }

    case "control_matrix": {
      const matrix = q.matrix || matrixSpec?.[q.qid];
      if (!matrix) return null;

      const { rows = [], cols = [] } = matrix;
      const matrixAnswers = Array.isArray(highlightedAnswers)
        ? highlightedAnswers
        : [];

      return (
        <article className="survey2col__question">
          <header className="survey2col__question-head">
            <h4 className="survey2col__question-title" id={legendId}>
              {text}
            </h4>
          </header>
          <div className="survey2col__matrix-wrapper">
            <table className="survey2col__matrix">
              <thead>
                <tr>
                  <th></th>
                  {cols.map((col, ci) => (
                    <th key={ci} className="survey2col__matrix-col">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri}>
                    <th className="survey2col__matrix-row">{row}</th>
                    {cols.map((col, ci) => {
                      const picked =
                        matrixAnswers[ri] &&
                        matrixAnswers[ri].toString().trim().toLowerCase() ===
                          col.toString().trim().toLowerCase();
                      return (
                        <td
                          key={ci}
                          className={`survey2col__matrix-cell ${
                            picked ? "is-selected" : ""
                          }`}
                        >
                          {picked ? "●" : ""}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>
      );
    }

    case "control_scale": {
      let scaleValue = "";
      if (highlightedAnswers instanceof Set) {
        const vals = Array.from(highlightedAnswers);
        if (vals.length > 0) scaleValue = vals[0];
      } else if (Array.isArray(highlightedAnswers)) {
        scaleValue =
          highlightedAnswers.find((v) => v && String(v).trim() !== "") || "";
      } else if (
        typeof highlightedAnswers === "string" &&
        highlightedAnswers.trim() !== ""
      ) {
        scaleValue = highlightedAnswers.trim();
      }

      return (
        <article className="survey2col__question">
          <header className="survey2col__question-head">
            <h4 className="survey2col__question-title">{text}</h4>
          </header>
          {scaleValue ? (
            <p style={{ color: "#27AE60", fontWeight: 700, fontSize: "1.1em" }}>
              {scaleValue}
            </p>
          ) : null}
        </article>
      );
    }

    case "control_number": {
      let numberValue = "";
      if (highlightedAnswers instanceof Set) {
        const vals = Array.from(highlightedAnswers);
        if (vals.length > 0) numberValue = vals[0];
      } else if (typeof highlightedAnswers === "string") {
        numberValue = highlightedAnswers;
      }
      if (!numberValue || String(numberValue).trim() === "") return null;

      return (
        <article className="survey2col__question">
          <h4 className="survey2col__question-title">{text}</h4>
          <p style={{ color: "#27AE60", fontWeight: 700 }}>{numberValue}</p>
        </article>
      );
    }

    case "control_dropdown": {
      const cleanSet =
        highlightedAnswers instanceof Set
          ? new Set(Array.from(highlightedAnswers).map((v) => clean(v)))
          : new Set(
              typeof highlightedAnswers === "string" &&
                highlightedAnswers.trim() !== ""
                ? [clean(highlightedAnswers)]
                : []
            );

      return (
        <article className="survey2col__question">
          <h4 className="survey2col__question-title">{text}</h4>
          {q.choices?.length ? (
            <ul
              className="survey2col__choices"
              style={{ listStyle: "none", margin: 0, paddingLeft: 0 }}
            >
              {q.choices.map((opt, idx) => {
                const isPicked = cleanSet.has(clean(opt));
                return (
                  <li key={idx}>
                    <span style={isPicked ? styleSelected : undefined}>
                      {opt}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </article>
      );
    }

    default:
      return null;
  }
}

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/dev";

export default function TwoColumnSurvey({ namespace, surveySessionId, token }) {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [active, setActive] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [matrixSpec, setMatrixSpec] = useState({});
  const [responsesByKey, setResponsesByKey] = useState({});
  const [sessionResponses, setSessionResponses] = useState(null);
  const [mode2020, setMode2020] = useState(false);
  const [mode2022, setMode2022] = useState(false);
  const [mode2025, setMode2025] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const ns = namespace?.toUpperCase();
        const is2025 = ns === "BAAS25E" || ns === "BAS25E";
        const is2020 = ns === "BAAS20E" || ns === "BAS20E";
        const file = is2025
          ? "/Form/survey_2025.json"
          : is2020
          ? "/Form/survey_2020.json"
          : "/Form/survey_2022.json";

        const res = await fetch(file, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        if (is2025) {
          setMode2025(true);
        } else if (is2020) {
          setMode2020(true);
        } else {
          setMode2022(true);
        }
        setData(json);

        if (is2020) {
          const byTitle = {};
          json.forEach(q => {
            byTitle[normalizeKey(q.title)] = q;
          });
          setResponsesByKey(byTitle);
        }

        if (surveySessionId) {
          const headers = { Accept: "application/json" };
          if (token) headers.Authorization = `Bearer ${token}`;
          const sessionRes = await fetch(
            `${API_BASE_URL}/surveySession/${surveySessionId}`,
            { headers }
          );
          const sessionJson = await sessionRes.json();

          const responses =
            sessionJson?.responses ||
            sessionJson?.Response ||
            sessionJson?.response ||
            [];
          
          setSessionResponses(responses);

          const byKey = {};

          // IMPORTANT : ne pas toucher au comportement 2022 / 2025
          // On ne construit responsesByKey que pour ces années.
          if (!is2020) {
            if (Array.isArray(responses)) {
              responses.forEach((r) => {
                const qNum = r?.question?.num;
                const ans = r?.answer;

                if (!qNum) return;
                if (ans == null) return;

                // simple string / number
                if (
                  (typeof ans === "string" || typeof ans === "number") &&
                  String(ans).trim() !== ""
                ) {
                  byKey[qNum] = String(ans).trim();
                  return;
                }

                // tableau d'objets { answered, label/value } (cas 2022/2025)
                if (Array.isArray(ans)) {
                  const selected = new Set();
                  ans.forEach((a) => {
                    if (a?.answered && (a?.label || a?.value)) {
                      selected.add(a.label || a.value);
                    } else if (a?.value && !("answered" in a)) {
                      // compat: certains backends n'ont pas "answered"
                      selected.add(a.value);
                    }
                  });
                  if (selected.size > 0) {
                    byKey[qNum] = selected;
                  }
                }
              });
            } else if (responses && typeof responses === "object") {
              Object.entries(responses).forEach(([key, val]) => {
                if (val == null) return;
                let cleanKey = key.replace(/^q/i, "");
                const m = cleanKey.match(/^(\d+)[^[]*(?:\[(\d+)\])?$/);
                if (!m) return;
                const qid = String(m[1]);
                const rowIdx =
                  m[2] !== undefined ? parseInt(m[2], 10) : null;

                if (rowIdx !== null) {
                  if (!Array.isArray(byKey[qid])) byKey[qid] = [];
                  byKey[qid][rowIdx] = String(val).trim();
                } else {
                  if (!byKey[qid] || Array.isArray(byKey[qid])) {
                    byKey[qid] = new Set();
                  }
                  if (typeof val === "string" && val.trim() !== "") {
                    byKey[qid].add(val.trim());
                  } else if (Array.isArray(val)) {
                    val.forEach((v) => {
                      const s = String(v).trim();
                      if (s !== "") byKey[qid].add(s);
                    });
                  }
                }
              });
            }
          }

          if (!is2020) {
  setResponsesByKey(byKey);
}
        }

        if (is2025) {
          const spec = await loadMatrixSpecFromFormHtml();
          setMatrixSpec(spec);
        }
      } catch (e) {
        console.error("Erreur SurveyTableComponent:", e);
        setErr("Impossible de charger le questionnaire.");
      } finally {
        setLoading(false);
      }
    })();
  }, [namespace, surveySessionId, token]);

  const sections = useMemo(() => {

    // 🟢 MODE 2020 : on part directement du tableau Response du backend
    if (mode2020 && Array.isArray(sessionResponses)) {

      const grouped = {};
      let currentTheme = "Caractéristiques de l'entreprise";

      const hasAnswer2020 = (resp) => {
        const ans = resp?.answer;
        if (ans == null) return false;

        if (Array.isArray(ans)) {
          return ans.some(
            (a) =>
              a &&
              a.value !== undefined &&
              a.value !== null &&
              String(a.value).trim() !== ""
          );
        }

        if (typeof ans === "number") return true;
        if (typeof ans === "string") return ans.trim() !== "";
        return false;
      };

      sessionResponses.forEach((resp) => {
        const q = resp?.question;
        if (!q) return;

        // callback technique : on ne l'affiche pas
        if (q.type === "string") return;

        if (q.themeName) {
          currentTheme = q.themeName;
        }

        if (!hasAnswer2020(resp)) return;

        if (!grouped[currentTheme]) grouped[currentTheme] = [];
        grouped[currentTheme].push(resp);
      });

      const result = Object.entries(grouped).map(([section, questions]) => ({
        section,
        questions
      }));

      return result;
    }

    // 🟢 MODE 2022 (comportement existant conservé)
    if (mode2022) {
      const grouped = (data || []).reduce((acc, q) => {
        const theme = q.themeName || "Divers";
        (acc[theme] = acc[theme] || []).push(q);
        return acc;
      }, {});
      const result = Object.entries(grouped)
        .map(([section, questions]) => {
          const answeredQs = questions.filter((q) => {
            const r = responsesByKey[q.num];
            if (!r) return false;
            if (r instanceof Set) return r.size > 0;
            if (typeof r === "string") return r.trim() !== "";
            if (Array.isArray(r)) {
              return r.some((v) => v && String(v).trim() !== "");
            }
            return false;
          });
          if (!answeredQs.length) return null;
          return { section, questions: answeredQs };
        })
        .filter(Boolean);
      return result;
    }

    // 🟢 MODE 2025 (comportement existant conservé)
    if (mode2025) {
      const arr = Array.isArray(data) ? data : [];
      const result = arr
        .map((sec) => {
          const qs = (sec?.questions || []).filter((q) => {
            const r = responsesByKey[q.qid];
            if (!r) return false;
            if (Array.isArray(r)) {
              return r.some((v) => v && String(v).trim() !== "");
            }
            if (r instanceof Set) return r.size > 0;
            if (typeof r === "string") return r.trim() !== "";
            return false;
          });
          return { section: sec.section, questions: qs };
        })
        .filter((sec) => (sec.questions || []).length > 0);
      return result;
    }

    return [];
  }, [data, mode2020, mode2022, mode2025, responsesByKey, sessionResponses]);

  useEffect(() => {
    setActive((i) => {
      if (i < 0) return 0;
      if (i >= sections.length) return Math.max(0, sections.length - 1);
      return i;
    });
  }, [sections.length]);

  const activeSection = useMemo(
    () => sections[active] || { section: "", questions: [] },
    [sections, active]
  );

  if (loading) return <p style={{ padding: 16 }}>Chargement…</p>;
  if (err) return <p style={{ padding: 16, color: "#b71c1c" }}>{err}</p>;

  if (sections.length === 0) {
    return (
      <div>
        <h2 className="survey2col__title">
          <img src="/icon-text.svg" alt="" />
          {t("survey.answers.title")}
        </h2>
        <p style={{ padding: 16 }}>{t("survey.answers.none")}</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="survey2col__title">
        <img src="/icon-text.svg" alt="" />
        {t("survey.answers.title")}
      </h2>

      <div className="survey2col">
        <aside className="survey2col__sidebar">
          <nav className="survey2col__nav" aria-label="Sections du questionnaire">
            {sections.map((sec, i) => (
              <button
                key={`${sec.section}-${i}`}
                className={`survey2col__navitem ${
                  i === active ? "is-active" : ""
                }`}
                onClick={() => setActive(i)}
                type="button"
              >
                <span>{sec.section}</span>
              </button>
            ))}
          </nav>
        </aside>

        <main className="survey2col__content">
          <h3 className="survey2col__section">{activeSection.section}</h3>

          <div className="survey2col__list">
            {activeSection.questions.map((q) => {
              // 🔹 Rendu spécifique 2020 (on part de resp { question, answer })
              if (mode2020) {
                const resp = q;
                const question = resp.question || {};
                const num = question.num;
                const title = normalizeText(question.title);
                const ans = resp.answer;

                if (question.type === "number" || question.type === "date") {
                  if (ans == null || ans === "") return null;
                  return (
                    <article
                      key={num}
                      className="survey2col__question"
                    >
                      <header className="survey2col__question-head">
                        <h4 className="survey2col__question-title">
                          {num}. {title}
                        </h4>
                      </header>
                      <ul
                        className="survey2col__choices"
                        style={{
                          listStyle: "none",
                          margin: 0,
                          paddingLeft: 0
                        }}
                      >
                        <li>
                          <span
                            style={{
                              color: "#27AE60",
                              fontWeight: 700
                            }}
                          >
                            {String(ans)}
                          </span>
                        </li>
                      </ul>
                    </article>
                  );
                }

                const surveyQ = responsesByKey[normalizeKey(title)];

if (!surveyQ) {
  console.warn(
  "[2020][NOT FOUND] title=", title,
  " normalized=", normalizeKey(title),
  " keys available=", Object.keys(responsesByKey).slice(0,50)
);

  return null;
}

const allOptions = surveyQ.responses || [];


                const selectedValues = new Set();
                

                if (Array.isArray(ans)) {
                  ans.forEach((a) => {
                    if (!a) return;
                    const v =
                      a.value !== undefined && a.value !== null
                        ? String(a.value).trim()
                        : "";
                    if (v) selectedValues.add(v);
                  });
                }

                return (
                  <article
                    key={num}
                    className="survey2col__question"
                  >
                    <header className="survey2col__question-head">
                      <h4 className="survey2col__question-title">
                        {num}. {title}
                      </h4>
                    </header>
                    {allOptions.length ? (
                      <ul
                        className="survey2col__choices"
                        style={{
                          listStyle: "none",
                          margin: 0,
                          paddingLeft: 0
                        }}
                      >
                        {allOptions.map((opt, idx) => {
                          const label =
                            opt?.label ?? String(opt ?? "");
                          const isPicked = selectedValues.has(
                            String(label).trim()
                          );
                          return (
                            <li key={idx}>
                              <span
                                style={
                                  isPicked
                                    ? {
                                        color: "#27AE60",
                                        fontWeight: 700
                                      }
                                    : undefined
                                }
                              >
                                {label}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </article>
                );
              }

              // 🔹 Rendu 2022 (inchangé sauf corrections précédentes)
              if (mode2022) {
                const val = responsesByKey[q.num];
                return (
                  <article
                    key={q.num}
                    className="survey2col__question"
                  >
                    <header className="survey2col__question-head">
                      <h4 className="survey2col__question-title">
                        {q.num}. {normalizeText(q.title)}
                      </h4>
                    </header>
                    <ul
                      className="survey2col__choices"
                      style={{
                        listStyle: "none",
                        margin: 0,
                        paddingLeft: 0
                      }}
                    >
                      {(() => {
                        if (!val) return null;
                        if (val instanceof Set && q.responses?.length) {
                          return q.responses.map((r, i) => {
                            const label =
                              r?.label ?? String(r ?? "");
                            const picked = val.has(label);
                            return (
                              <li key={i}>
                                <span
                                  style={
                                    picked
                                      ? {
                                          color: "#27AE60",
                                          fontWeight: 700
                                        }
                                      : undefined
                                  }
                                >
                                  {label}
                                </span>
                              </li>
                            );
                          });
                        }
                        if (val instanceof Set) {
                          return Array.from(val).map((v, i) => (
                            <li key={i}>
                              <span
                                style={{
                                  color: "#27AE60",
                                  fontWeight: 700
                                }}
                              >
                                {v}
                              </span>
                            </li>
                          ));
                        }
                        return (
                          <li>
                            <span
                              style={{
                                color: "#27AE60",
                                fontWeight: 700
                              }}
                            >
                              {val}
                            </span>
                          </li>
                        );
                      })()}
                    </ul>
                  </article>
                );
              }

              // 🔹 Rendu 2025 (QuestionItem existant)
              return (
                <QuestionItem
                  key={q.qid}
                  q={q}
                  value={answers[q.name || `q_${q.qid}`]}
                  onChange={(name, val) =>
                    setAnswers((prev) => ({ ...prev, [name]: val }))
                  }
                  matrixSpec={matrixSpec}
                  highlightedAnswers={responsesByKey[q.qid]}
                />
              );
            })}
          </div>

          <div className="survey2col__pager">
            <button
              type="button"
              className="survey2col__pager-btn"
              onClick={() =>
                setActive((i) => Math.max(0, i - 1))
              }
              disabled={active === 0}
            >
              {t("survey.navigation.prev")}
            </button>
            <button
              type="button"
              className="survey2col__pager-btn survey2col__pager-btn--primary"
              onClick={() =>
                setActive((i) =>
                  Math.min(sections.length - 1, i + 1)
                )
              }
              disabled={active === sections.length - 1}
            >
              {t("survey.navigation.next")}
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
